import { Fragment, useId, useState, useEffect, type ReactNode } from "react";
import type { Answer, Field, ParticipantStep } from "../../shared/types.ts";
import {
  SingleSelectInput,
  MultiSelectInput,
  multiSelectHint,
} from "../../../vendor/member-core/components/FormExecutor/SelectInputs.tsx";
import { ScaleVisual } from "../../../vendor/member-core/components/ScaleVisual.tsx";
import { PAPER_INPUT } from "../../../vendor/member-core/components/FormExecutor/inputStyles.ts";
import "./participant-fidelity.css";

/** Source copy marks emphasis as __words__; everything else renders verbatim. */
export function RichText({ text }: { text: string }) {
  return (
    <>
      {text
        .split(/__(.+?)__/g)
        .map((part, index) =>
          index % 2 ? (
            <u key={index}>{part}</u>
          ) : (
            <Fragment key={index}>{part}</Fragment>
          ),
        )}
    </>
  );
}

export function Eyebrow({ step }: { step: ParticipantStep }) {
  const text =
    step.eyebrow ?? (step.title !== step.sectionTitle ? step.title : undefined);
  return text ? <p className="pe-eyebrow">{text}</p> : null;
}

export function hasAnswer(value: Answer | undefined): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export function sameAnswer(
  a: Answer | undefined,
  b: Answer | undefined,
): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

export interface FieldProps {
  field: Field;
  resetKey?: number;
  value: Answer | undefined;
  disabled: boolean;
  /** Several fields share the screen, so short choices can sit in rows. */
  grouped: boolean;
  /** Set when the step heading already is this field's question. */
  labelledBy?: string;
  describedBy?: string;
  lowLabel?: string;
  highLabel?: string;
  onCommit: (value: Answer) => void;
  onDraft: (value: string) => void;
}

export function ParticipantField(props: FieldProps) {
  switch (props.field.kind) {
    case "select":
    case "multi":
      return <ChoiceField {...props} />;
    case "rating":
      return <RatingField {...props} />;
    case "count":
      return <CountField {...props} />;
    case "text":
      return <TextField {...props} />;
  }
}

function Group({
  field,
  labelledBy,
  describedBy,
  children,
  disabled,
  legendId,
  presentation = false,
}: Pick<FieldProps, "field" | "labelledBy" | "describedBy" | "disabled"> & {
  children: ReactNode;
  legendId?: string;
  presentation?: boolean;
}) {
  return (
    <fieldset
      className="pe-field"
      role={presentation ? "presentation" : undefined}
      disabled={disabled}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
    >
      {!labelledBy && (
        <legend id={legendId} className="pe-field__label">
          <RichText text={field.label} />
        </legend>
      )}
      {children}
    </fieldset>
  );
}

function ChoiceField({
  field,
  value,
  disabled,
  grouped,
  labelledBy,
  describedBy,
  onCommit,
}: FieldProps) {
  const hintId = useId(),
    legendId = useId();
  const variant = grouped ? "clustered" : "spotlight";
  const options = (field.options ?? []).map((option) => ({
    ...option,
    label: option.label.replaceAll("__", ""),
  }));
  // Old run records predate explicit choiceLayout. Binary source values retain
  // their compact register; all other missing metadata uses the standard list.
  const binary =
    field.choiceLayout === "binary" ||
    (!field.choiceLayout &&
      options.length === 2 &&
      options.every((o) => /^(yes|no)$/i.test(o.value)));
  const oldLikert =
    !field.choiceLayout &&
    options.some((o) => /^strongly agree$/i.test(o.value)) &&
    options.some((o) => /^strongly disagree$/i.test(o.value));
  const hint =
    field.kind === "multi"
      ? multiSelectHint({
          required: field.required,
          min_selections: field.min,
          max_selections: field.max,
        })
      : undefined;
  return (
    <Group
      field={field}
      disabled={disabled}
      legendId={legendId}
      presentation={field.kind === "multi"}
      labelledBy={labelledBy}
      describedBy={
        [describedBy, hint ? hintId : undefined].filter(Boolean).join(" ") ||
        undefined
      }
    >
      {hint && (
        <p id={hintId} className="pe-multi-hint">
          {hint}
        </p>
      )}
      <div
        data-source-control={
          field.kind === "multi" ? "multi-select" : "single-select"
        }
      >
        {field.kind === "multi" ? (
          <MultiSelectInput
            options={options}
            labelledById={labelledBy ?? legendId}
            describedById={hint ? hintId : describedBy}
            value={value}
            variant={variant}
            maxSelections={field.max}
            fieldKey={field.id}
            onChange={(next) => {
              if (!disabled) onCommit(next as Answer);
            }}
          />
        ) : (
          <SingleSelectInput
            options={options}
            labelledById={labelledBy ?? legendId}
            value={value}
            variant={variant}
            segmented={
              binary || oldLikert || field.choiceLayout === "segmented"
            }
            fieldKey={field.id}
            onChange={(next) => {
              if (!disabled) onCommit(next as Answer);
            }}
          />
        )}
      </div>
    </Group>
  );
}

function RatingField({
  field,
  value,
  disabled,
  labelledBy,
  describedBy,
  lowLabel,
  highLabel,
  resetKey,
  onCommit,
}: FieldProps) {
  const labelId = useId(),
    comparisonId = useId();
  const min = field.min ?? 0,
    max = Math.max(min, field.max ?? min);
  const score = typeof value === "number" ? value : null;
  const comparison = field.comparison;
  const description =
    [describedBy, comparison ? comparisonId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;
  const reference = comparison ? (
    <p
      id={comparisonId}
      className="pe-rating-comparison"
      data-testid="rating-comparison"
    >
      <span>
        <RichText text={comparison.label} />
      </span>
      <strong>{comparison.value}</strong>
      <span className="pe-sr">out of {max}</span>
    </p>
  ) : null;
  if (max === min)
    return (
      <Group
        field={field}
        disabled={disabled}
        labelledBy={labelledBy}
        describedBy={description}
        legendId={labelId}
      >
        {reference}
        <SingleSelectInput
          options={[{ value: String(min), label: String(min) }]}
          value={score === null ? null : String(score)}
          variant="spotlight"
          onChange={() => {
            if (!disabled) onCommit(min);
          }}
        />
      </Group>
    );
  return (
    <Group
      field={field}
      disabled={disabled}
      labelledBy={labelledBy}
      describedBy={description}
      legendId={labelId}
    >
      {reference}
      <div data-source-control="scale" inert={disabled}>
        <ScaleVisual
          key={disabled ? "mirror:" + score : "input:" + resetKey}
          config={{
            min,
            max,
            step: 1,
            min_label: field.lowLabel ?? lowLabel ?? "",
            max_label: field.highLabel ?? highLabel ?? "",
          }}
          mode="interactive"
          score={score}
          requiresMemberInput={score === null}
          animateEntrance={false}
          ariaLabelledby={labelledBy ?? labelId}
          anchorId={field.id}
          onScoreCommit={(next) => {
            if (!disabled) onCommit(next);
          }}
        />
      </div>
      {disabled && (
        <p className="pe-sr" role="status">
          {score === null ? "No score selected" : `${score} out of ${max}`}
        </p>
      )}
    </Group>
  );
}

function CountField({
  field,
  value,
  disabled,
  labelledBy,
  describedBy,
  onCommit,
  onDraft,
}: FieldProps) {
  const inputId = useId(),
    errorId = useId();
  const [typed, setTyped] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const min = field.min ?? 0,
    max = field.max ?? Number.MAX_SAFE_INTEGER;
  const current = typeof value === "number" ? value : null;
  useEffect(() => {
    if (typeof value !== "string") setTyped(null);
  }, [value]);
  const settle = () => {
    if (typed === null) return;
    const text = typed.trim(),
      next = Number(text);
    if (text === "") {
      if (field.required) {
        setInvalid(true);
        return;
      }
      setTyped(null);
      setInvalid(false);
      onCommit(null);
      return;
    }
    if (text !== "" && (!Number.isInteger(next) || next < min || next > max)) {
      setInvalid(true);
      return;
    }
    setTyped(null);
    setInvalid(false);
    if (text !== "" && next !== current) onCommit(next);
  };
  return (
    <div className="pe-field" data-source-control="number">
      {!labelledBy && (
        <label className="pe-field__label" htmlFor={inputId}>
          <RichText text={field.label} />
        </label>
      )}
      <input
        id={inputId}
        className={PAPER_INPUT + " tabular-nums"}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={1}
        value={typed ?? (typeof value === "string" ? value : current) ?? ""}
        disabled={disabled}
        aria-labelledby={labelledBy}
        aria-describedby={
          [describedBy, invalid ? errorId : undefined]
            .filter(Boolean)
            .join(" ") || undefined
        }
        aria-invalid={invalid || undefined}
        onChange={(event) => {
          setTyped(event.target.value);
          setInvalid(false);
          onDraft(event.target.value);
        }}
        onBlur={settle}
        onKeyDown={(event) => {
          if (event.key === "Enter") settle();
        }}
      />
      {field.countUnit && <p className="pe-count-note">{field.countUnit}</p>}
      {field.countNote && (
        <p className="pe-count-note">
          <RichText text={field.countNote} />
        </p>
      )}
      {invalid && (
        <p id={errorId} className="pe-field__error" role="alert">
          Enter a whole number from {min} to {max}.
        </p>
      )}
    </div>
  );
}

function TextField({
  field,
  value,
  disabled,
  labelledBy,
  describedBy,
  onDraft,
  onCommit,
}: FieldProps) {
  const inputId = useId();
  return (
    <div className="pe-field">
      {!labelledBy && (
        <label className="pe-field__label" htmlFor={inputId}>
          <RichText text={field.label} />
        </label>
      )}
      <textarea
        id={inputId}
        className={
          PAPER_INPUT +
          " min-h-[120px] resize-none leading-[1.6] [field-sizing:content]"
        }
        rows={4}
        maxLength={10000}
        value={typeof value === "string" ? value : ""}
        disabled={disabled}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        onChange={(event) => onDraft(event.target.value)}
        onBlur={(event) => onCommit(event.target.value)}
      />
    </div>
  );
}
